import { Dispatch, SetStateAction, useCallback, useEffect, useState } from 'react';

import { selectFontPacks, selectThemePacks } from '@/store/slices/templates/selectors';
import { useAppDispatch, useAppSelector } from '@/store/hooks';
import GlobalTemplatesChannel from '@ui/auth-wrapper/helpers/global-templates-channel';
import toastTrigger from '@ui/toast/toast-trigger';
import Text from '@ui/text/text/text';
import Modal from '@ui/modal/modal';
import Button from '@ui/buttons/button/button';
import Checkbox from '@ui/checkbox/checkbox';
import { FontPack, ThemePack } from '@/types/theme-packs.types';
import { UpdateThemePackAsGlobal } from '@/connection/theme-packs';
import { setFontPack, setThemePack } from '@/store/slices/templates';
import isGlobalThemePack from '@/utils/is-global-theme-pack';
import { EToastTypes } from '@ui/toast/toast.types';

type Props = {
  setShowMarkAsGlobalModal: Dispatch<SetStateAction<ThemePack | null>>;
  themePack: ThemePack | null;
};

const GlobalThemeModal = ({ setShowMarkAsGlobalModal, themePack }: Props) => {
  const dispatch = useAppDispatch();

  const themePacks = useAppSelector(selectThemePacks);
  const fontPacks = useAppSelector(selectFontPacks);

  const [savingGlobalPackUpdate, setSavingGlobalPackUpdate] = useState(false);
  const [markAsGlobal, setMarkAsGlobal] = useState(isGlobalThemePack(themePack));

  useEffect(() => {
    if (!themePack || !('channel' in themePack)) {
      return setMarkAsGlobal(false);
    }
    setMarkAsGlobal(isGlobalThemePack(themePack));
  }, [themePack]);

  const handleCancelThemePackModal = useCallback(() => {
    if (savingGlobalPackUpdate) return;

    setShowMarkAsGlobalModal(null);
  }, [savingGlobalPackUpdate, setShowMarkAsGlobalModal]);

  const handleUpdatePackChannel = useCallback(async () => {
    try {
      if (!themePacks || !fontPacks || !themePack) return;

      setSavingGlobalPackUpdate(true);

      await UpdateThemePackAsGlobal({
        themePackId: themePack.color_pack,
        isGlobal: markAsGlobal,
      });

      const newChannel = markAsGlobal ? 0 : GlobalTemplatesChannel.channel;
      const _newChannel = newChannel ?? themePack.channel;

      const updatedThemePack = {
        ...themePack,
        channel: _newChannel,
        creation_type: _newChannel === 0 ? null : themePack.creation_type,
      };

      const matchedFontPack = fontPacks.find((fp) => fp.font_pack === themePack.font_pack?.font_pack);
      const updatedFontPack: FontPack | null = matchedFontPack
        ? {
            ...matchedFontPack,
            channel: newChannel ?? matchedFontPack.channel,
          }
        : null;

      // update color packs list in redux (make sure to update in both templates and content slices)
      dispatch(setThemePack(updatedThemePack));
      if (updatedFontPack) {
        dispatch(setFontPack(updatedFontPack));
      }
    } catch (e) {
      console.error(e);
      toastTrigger('Failed to update theme pack global setting. Please refresh and try again.', {
        type: EToastTypes.error,
      });
    } finally {
      setSavingGlobalPackUpdate(false);
      setShowMarkAsGlobalModal(null);
    }
  }, [dispatch, fontPacks, markAsGlobal, setShowMarkAsGlobalModal, themePack, themePacks]);

  return (
    <Modal
      open={!!themePack}
      size="large"
      onRequestClose={handleCancelThemePackModal}
      RenderHeaderContent={() => (
        <Text tag="h4" className="H4 padding-16">
          Make theme pack global
        </Text>
      )}
      RenderFooterContent={() => (
        <div className="flex jc-flex-end ai-center gap-16 padding-16">
          <Button
            design="LocalBttnSecHalfTransp"
            onClick={handleCancelThemePackModal}
            disabled={savingGlobalPackUpdate}
          >
            Cancel
          </Button>
          <Button
            design="LocalBttnPrim"
            onClick={handleUpdatePackChannel}
            disabled={savingGlobalPackUpdate}
            loading={savingGlobalPackUpdate}
          >
            Save
          </Button>
        </div>
      )}
    >
      <div className="padding-16">
        <Checkbox
          value="global-themepack"
          checked={markAsGlobal}
          onChange={() => setMarkAsGlobal((prev) => !prev)}
          label="Make theme pack global"
          disabled={savingGlobalPackUpdate}
        />
        <div>Theme Pack will behave the same as the existing default packs (Amethyst, Bold, Classic, Fun):</div>
        <ul>
          <li>Will become uneditable once marked as global.</li>
          <li>Will become accessible to every channel.</li>
          <li>Will also set the associated font pack to become global.</li>
        </ul>

        <strong>
          <u>IMPORTANT</u> - "un"globalizing a theme pack can have unintended side effects for ANY project on ANY
          channel that uses the theme pack. Therefore, it is highly recommended to never uncheck unless it's for a few
          seconds to update a theme pack's colors.
        </strong>
      </div>
    </Modal>
  );
};

export default GlobalThemeModal;
